<script lang="ts" setup>
  import { computed } from 'vue';
  import { useShared } from '@othercommerce/foundation';
  import type { Unit } from '@othercommerce/domain';
  import { useUnitFormatter } from '@othercommerce/domain';

  interface Price {
    stockUnit: Unit;
    stockUnitBase: string; // DECIMAL(8,4)
    multipackUnitPriceNet: string; // DECIMAL(19,4)
    multipackUnitPriceGross: string; // DECIMAL(19,4)
  }

  interface Props {
    price: Price;
  }

  const props = defineProps<Props>();
  const shared = useShared();
  const unitFormatter = useUnitFormatter();

  const price = computed(() => {
    if (shared.value.includeVat) {
      return props.price.multipackUnitPriceGross;
    } else {
      return props.price.multipackUnitPriceNet;
    }
  });

  const unit = computed(() => {
    let stockUnit = props.price.stockUnit;
    let stockBase = props.price.stockUnitBase;

    if (stockUnit && stockBase) {
      return unitFormatter.accusative(stockUnit, stockBase);
    }

    return undefined;
  });

  const visible = computed(() => {
    return price.value !== '0.0000' && unit.value;
  });
</script>
<template>
  <div v-if="visible && unit && price" class="unit-price">
    <span v-html="$t('SHOP.COMMON.LABEL.UNIT_PRICE_IN_SET', { unit })" />
    <SfMoney :value="price" />
  </div>
</template>
<style lang="scss">
  :root {
    --multipack-unit-price-color-text: var(--color-default);
    --multipack-unit-price-font-family: var(--font-family-sans);
    --multipack-unit-price-font-size: var(--font-size-sm);
    --multipack-unit-price-font-weight: var(--font-weight-regular);
    --multipack-unit-price-padding-y: 0;
    --multipack-unit-price-padding-x: 0;
    --multipack-unit-price-margin-y: 0;
    --multipack-unit-price-margin-x: 0;
  }
</style>
<style lang="scss" scoped>
  .unit-price {
    font-size: var(--multipack-unit-price-font-size);
    font-family: var(--multipack-unit-price-font-family);
    color: var(--multipack-unit-price-color-text);
    font-weight: var(--multipack-unit-price-font-weight);
    padding: var(--multipack-unit-price-padding-y) var(--multipack-unit-price-padding-x);
    margin: var(--multipack-unit-price-margin-y) var(--multipack-unit-price-margin-x);
  }
</style>
